import React, { useEffect } from 'react';
import { Form, Input, Button, Select, Space, Card, Typography, Badge, Tooltip } from 'antd';
import {
  InfoCircleOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { createSection } from '../../constants';
import { getRiskColor } from '../../../../utils';

interface ProjectTypeExamplesProps {
  setData: (path: string, value: any) => void;
  data: any;
  riskDriverId?: string;
  contentItemId: string;
  sectionKey: string;
  readonly?: boolean;
  dataKey: string;
}

const ProjectTypeExamples: React.FC<ProjectTypeExamplesProps> = ({
  setData,
  data,
  riskDriverId,
  contentItemId,
  sectionKey,
  dataKey,
  readonly = false
}) => {
  const [form] = Form.useForm();
  const options = [
    { value: 'low', label: 'Lower risk' },
    { value: 'high', label: 'Higher risk' }
  ];
  useEffect(() => {
    let initialValues = { riskLevels: [] };

    if (dataKey === 'riskDrivers' && riskDriverId) {
      const riskDriver = data.riskDrivers.find((driver: any) => driver.id === riskDriverId);
      if (riskDriver) {
        const contentItem = riskDriver.data.find((item: any) => item.id === contentItemId);
        if (contentItem && contentItem.data) {
          initialValues = { riskLevels: contentItem.data };
        }
      }
    } else if (dataKey === 'description') {
      const contentItem = data.description.find((item: any) => item.id === contentItemId);
      if (contentItem && contentItem.data) {
        initialValues = { riskLevels: contentItem.data };
      }
    }

    if (initialValues.riskLevels.length === 0) {
      form.setFieldsValue({
        riskLevels: [
          {
            impact: 'low',
            riskLevel: 'Lower risk',
            description: '',
            projects: [{ type: '', examplesNote: '', examples: [{ label: '', url: '' }] }]
          }
        ]
      });
    } else {
      form.setFieldsValue(initialValues);
    }
  }, []);

  const handleValuesChange = (changedValues, allValues) => {
    if (readonly) return; // Prevent changes in readonly mode
    const values = allValues.riskLevels;
    values.forEach((riskLevel: any) => {
      if (riskLevel.impact) {
        riskLevel.riskLevel = options.find(
          (option: any) => option.value === riskLevel.impact
        )?.label;
      }
    });

    if (dataKey === 'riskDrivers') {
      const updatedRiskDrivers = data.riskDrivers.map((driver: any, i: number) => {
        if (driver.id === riskDriverId) {
          driver.data.forEach((item: any, j: number) => {
            if (item.id === contentItemId) {
              item.data = values;
              if ((item.data || [])?.length === 0 || !item.data || !item.id) {
                item.id = createSection('table').id;
              }
            }
          });
          return driver;
        }
        return driver;
      });
      setData(`${sectionKey}.riskDrivers`, updatedRiskDrivers);
    } else {
      const updatedDescription = data.description.map((item: any) => {
        if (item.id === contentItemId) {
          item.data = values;
        }
        return item;
      });
      setData(`${sectionKey}.description`, updatedDescription);
    }
  };

  const validateUrl = (_, value) => {
    if (value && !/^https?:\/\//.test(value)) {
      return Promise.reject(new Error('Please enter an absolute URL path'));
    }
    return Promise.resolve();
  };

  return (
    <Form
      form={form}
      name="ghg_summary_form"
      autoComplete="off"
      layout="vertical"
      onValuesChange={handleValuesChange}>
      {/* Message container will be at the top level */}

      <Form.List name="riskLevels">
        {(fields, { add: addRiskLevel, remove: removeRiskLevel }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Card
                key={key}
                style={{ marginBottom: 24 }}
                title={`Risk Level ${name + 1}`}
                extra={
                  <Button
                    danger
                    disabled={readonly}
                    icon={<MinusCircleOutlined />}
                    onClick={() => removeRiskLevel(name)}>
                    Remove
                  </Button>
                }>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space align="baseline">
                    <Form.Item
                      {...restField}
                      name={[name, 'impact']}
                      label="Risk level"
                      rules={[{ required: true, message: 'Please select impact!' }]}>
                      <Select
                        style={{ width: '200px' }}
                        disabled={readonly}
                        options={options.map((option, index) => ({
                          key: option.value.toString(),
                          value: option.value.toString(),
                          label: (
                            <Badge
                              color={index === 0 ? 'green' : 'red'}
                              text={option.label}
                              key={option.value}
                            />
                          )
                        }))}
                      />
                    </Form.Item>
                  </Space>

                  {/* Projects Form List */}
                  <Form.List name={[name, 'projects']}>
                    {(projectFields, { add: addProject, remove: removeProject }) => (
                      <>
                        {projectFields.map(
                          ({ key: projectKey, name: projectName, ...projectRestField }) => (
                            <Card
                              key={projectKey}
                              type="inner"
                              title={`Project ${projectName + 1}`}
                              style={{ marginBottom: 16 }}
                              extra={
                                <Button
                                  danger
                                  disabled={readonly}
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => removeProject(projectName)}>
                                  Remove Project
                                </Button>
                              }>
                              <Form.Item
                                {...projectRestField}
                                name={[projectName, 'type']}
                                label="Project Type">
                                <Input.TextArea
                                  disabled={readonly}
                                  placeholder="Description of the project type"
                                  autoSize={{ minRows: 2, maxRows: 6 }}
                                />
                              </Form.Item>

                              <Form.Item
                                {...projectRestField}
                                name={[projectName, 'examplesNote']}
                                label="Explanatory text if no examples to show (optional)">
                                <Input.TextArea
                                  disabled={readonly}
                                  placeholder="e.g. Note about the examples"
                                  autoSize={{ minRows: 2, maxRows: 6 }}
                                />
                              </Form.Item>

                              {/* Examples Form List */}
                              <Form.List name={[projectName, 'examples']}>
                                {(exampleFields, { add: addExample, remove: removeExample }) => (
                                  <>
                                    {exampleFields.length > 0 && (
                                      <Typography.Text strong>Examples:</Typography.Text>
                                    )}
                                    {exampleFields.length === 0 && (
                                      <Button
                                        type="primary"
                                        disabled={readonly}
                                        onClick={() => addExample()}>
                                        Add Example
                                      </Button>
                                    )}
                                    {exampleFields.map(
                                      (
                                        { key: exampleKey, name: exampleName, ...exampleRestField },
                                        exampleIndex
                                      ) => (
                                        <Space
                                          key={exampleKey}
                                          style={{
                                            display: 'flex',
                                            marginBottom: 8,
                                            marginTop: 8
                                          }}
                                          align="baseline">
                                          <Form.Item
                                            {...exampleRestField}
                                            name={[exampleName, 'label']}>
                                            <Input
                                              disabled={readonly}
                                              placeholder="Label (e.g. VCS 961)"
                                            />
                                          </Form.Item>

                                          <Form.Item
                                            {...exampleRestField}
                                            name={[exampleName, 'url']}
                                            rules={[{ validator: validateUrl }]}>
                                            <Input
                                              disabled={readonly}
                                              style={{ width: '300px' }}
                                              prefix={<LinkOutlined />}
                                              suffix={
                                                <Tooltip title="Add executive summary link for the project">
                                                  <InfoCircleOutlined />
                                                </Tooltip>
                                              }
                                              placeholder="Enter url (only absolute path)"
                                            />
                                          </Form.Item>
                                          <Button
                                            type="primary"
                                            danger
                                            disabled={readonly}
                                            style={{ height: '100%' }}
                                            onClick={() => removeExample(exampleName)}>
                                            <MinusCircleOutlined />
                                          </Button>
                                          {exampleFields.length === exampleIndex + 1 && (
                                            <Button
                                              type="primary"
                                              disabled={readonly}
                                              onClick={() => addExample()}>
                                              <PlusOutlined />
                                            </Button>
                                          )}
                                        </Space>
                                      )
                                    )}
                                  </>
                                )}
                              </Form.List>
                            </Card>
                          )
                        )}

                        <Form.Item>
                          <Button
                            type="dashed"
                            disabled={readonly}
                            onClick={() => addProject()}
                            icon={<PlusOutlined />}
                            block>
                            Add Project
                          </Button>
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Space>
              </Card>
            ))}
            <Button
              type="primary"
              disabled={readonly}
              icon={<PlusOutlined />}
              onClick={() =>
                addRiskLevel({
                  riskLevel: 'Lower risk',
                  impact: 'low',
                  projects: [
                    {
                      type: '',
                      examplesNote: '',
                      examples: []
                    }
                  ]
                })
              }
              style={{ marginTop: 20, width: '100%' }}>
              Add Risk Level
            </Button>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default ProjectTypeExamples;
